import React, { useEffect, useMemo, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  onSnapshot,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../../firebase";

import PatientShell from "./PatientShell";

/* -------------------------------------------------------
   Helpers
------------------------------------------------------- */
const toJsDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v.toDate === "function") return v.toDate();
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

const formatDate = (ts) => {
  if (!ts) return "";
  const d = toJsDate(ts);
  if (!d) return "";
  return d.toLocaleDateString("sq-AL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatAppt = (date, time) => {
  if (!date) return "";
  const d = toJsDate(date);
  if (!d) return "";
  const ds = d.toLocaleDateString("sq-AL", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  return time ? `${ds} • ${time}` : ds;
};

/* -------------------------------------------------------
   MAIN CONTAINER
------------------------------------------------------- */
export default function Patient2Container() {
  const [user, authLoading] = useAuthState(auth);
  const [bootLoading, setBootLoading] = useState(true);
  const navigate = useNavigate();

  const [doctors, setDoctors] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [records, setRecords] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);

  /* -------------------------------------------------------
     1) Load doctors (no index issues)
  ------------------------------------------------------- */
  useEffect(() => {
    const q = query(collection(db, "doctors"), where("isActive", "==", true));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setDoctors(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (err) => console.error("doctors listener error", err)
    );
    return () => unsub();
  }, []);

  /* -------------------------------------------------------
     2) Load appointments (no orderBy, sort client-side)
  ------------------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "appointments"),
      where("patientId", "==", user.uid)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const da = toJsDate(a.scheduledDate)?.getTime() ?? 0;
          const dbb = toJsDate(b.scheduledDate)?.getTime() ?? 0;
          return dbb - da; // newest first
        });
        setAppointments(rows);
      },
      (err) => console.error("appointments listener error", err)
    );
    return () => unsub();
  }, [user]);

  /* -------------------------------------------------------
     3) Load medical records (no orderBy, sort client-side)
  ------------------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "medicalRecords"),
      where("patientId", "==", user.uid)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const da = toJsDate(a.createdAt)?.getTime() ?? 0;
          const dbb = toJsDate(b.createdAt)?.getTime() ?? 0;
          return dbb - da;
        });
        setRecords(rows);
      },
      (err) => console.error("medicalRecords listener error", err)
    );
    return () => unsub();
  }, [user]);

  /* -------------------------------------------------------
     4) Load uploaded documents (no orderBy, sort client-side)
  ------------------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "patient_documents"),
      where("patientId", "==", user.uid)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const da = toJsDate(a.uploadedAt)?.getTime() ?? 0;
          const dbb = toJsDate(b.uploadedAt)?.getTime() ?? 0;
          return dbb - da;
        });
        setDocuments(rows);
      },
      (err) => console.error("patient_documents listener error", err)
    );
    return () => unsub();
  }, [user]);

  /* -------------------------------------------------------
     5) Load reviews (no orderBy, sort client-side)
  ------------------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "reviews"),
      where("patientId", "==", user.uid)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const da = toJsDate(a.createdAt)?.getTime() ?? 0;
          const dbb = toJsDate(b.createdAt)?.getTime() ?? 0;
          return dbb - da;
        });
        setReviews(rows);
      },
      (err) => console.error("reviews listener error", err)
    );
    return () => unsub();
  }, [user]);

  /* -------------------------------------------------------
     6) Load unread messages (only where's → no composite index)
  ------------------------------------------------------- */
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, "conversations"),
      where("patientId", "==", user.uid),
      where("hasUnreadForPatient", "==", true)
    );
    const unsub = onSnapshot(
      q,
      (snap) => setUnreadMessages(snap.size),
      (err) => console.error("conversations listener error", err)
    );
    return () => unsub();
  }, [user]);

  /* -------------------------------------------------------
     7) Derived view models
  ------------------------------------------------------- */

  // upcoming / past appointments
  const appointmentsUpcoming = useMemo(() => {
    const now = new Date();
    return appointments.filter((a) => {
      const d = toJsDate(a.scheduledDate);
      return d && d >= now;
    });
  }, [appointments]);

  const appointmentsPast = useMemo(() => {
    const now = new Date();
    return appointments.filter((a) => {
      const d = toJsDate(a.scheduledDate);
      return d && d < now;
    });
  }, [appointments]);

  const nextAppointment = appointmentsUpcoming[0] || null;

  // doctors VM for Home + Explore
  const doctorsVM = useMemo(
    () =>
      doctors.map((d) => ({
        id: d.id,
        name: d.name || d.displayName || "Doktor",
        specialty: d.specialty || "",
        avatarUrl: d.photoUrl || "",
        rating: d.rating,
        ratingCount: d.ratingCount,
        locationLabel: d.city || "",
        priceLabel: d.minVisitPrice ? `Nga ${d.minVisitPrice} Lek` : "",
      })),
    [doctors]
  );

  // specialties
  const specialties = useMemo(
    () => Array.from(new Set(doctors.map((d) => d.specialty).filter(Boolean))),
    [doctors]
  );

  // documents VM
  const documentsVM = useMemo(
    () =>
      documents.map((doc) => ({
        id: doc.id,
        title: doc.title || doc.fileName || "Dokument",
        subtitle: doc.subtitle || doc.labName || "",
        dateLabel: formatDate(doc.uploadedAt),
      })),
    [documents]
  );

  // records VM (ready if you want to show them)
  const recordsVM = useMemo(
    () =>
      records.map((r) => ({
        id: r.id,
        doctorName: r.doctorInfo?.name,
        specialty: r.doctorInfo?.specialty,
        dateLabel: formatAppt(r.appointmentDate, r.appointmentTime),
      })),
    [records]
  );

  // pending reviews
  const reviewsPending = useMemo(() => {
    const reviewedIds = new Set(reviews.map((r) => r.appointmentId));
    return appointmentsPast.filter((a) => !reviewedIds.has(a.id));
  }, [appointmentsPast, reviews]);

  // recent reviews VM
  const reviewsRecent = useMemo(
    () =>
      reviews.map((r) => ({
        id: r.id,
        doctorName: r.doctorInfo?.name,
        specialty: r.doctorInfo?.specialty,
        comment: r.comment,
        dateLabel: formatDate(r.createdAt),
      })),
    [reviews]
  );

  // Recommended doctors: simple top 3
  const recommendedDoctors = useMemo(
    () => doctorsVM.slice(0, 3),
    [doctorsVM]
  );

  // mark boot done when auth ready
  useEffect(() => {
    if (!authLoading) setBootLoading(false);
  }, [authLoading]);

  /* -------------------------------------------------------
     Navigation handlers
  ------------------------------------------------------- */
  const handleOpenDoctorProfile = (item) => {
    // item could be doctorVM or a visit/appointment
    const doctorId = item.doctorId || item.id;
    if (!doctorId) return;
    navigate(`/doctor/${doctorId}`); // change if your route is different
  };

  const handleBookDoctor = (item) => {
    const doctorId = item.doctorId || item.id;
    if (!doctorId) return;
    navigate(`/book/${doctorId}`); // adjust path
  };

  const handleOpenAppointment = (appt) => {
    if (!appt?.id) return;
    navigate(`/appointments/${appt.id}`);
  };

  const handleOpenDocument = (doc) => {
    if (!doc?.id) return;
    navigate(`/patient/documents/${doc.id}`);
  };

  const handleOpenMessages = () => {
    navigate("/messages");
  };

  const handleWriteReview = (appt) => {
    if (!appt?.id) return;
    navigate(`/appointments/${appt.id}/review`);
  };

  
  const handleProfile = () => {
    navigate("/patient/profile");
  };

  /* -------------------------------------------------------
     Loading / auth gates
  ------------------------------------------------------- */
  if (authLoading || bootLoading) {
    return <div style={{ padding: 40 }}>Loading...</div>;
  }

  if (!user) {
    return <div style={{ padding: 40 }}>Please login…</div>;
  }

  /* -------------------------------------------------------
     Render Final Patient2 Shell
  ------------------------------------------------------- */
  return (
    <PatientShell
      userName={user.displayName || user.email?.split("@")[0] || "MikDok"}
      nextAppointment={nextAppointment}
      recentVisits={appointmentsPast.slice(0, 3)}
      recommendedDoctors={recommendedDoctors}
      specialties={specialties}
      doctors={doctorsVM}
      appointmentsUpcoming={appointmentsUpcoming}
      appointmentsPast={appointmentsPast}
      documents={documentsVM}
      // recordsVM is ready if you want to add it to MyHealthScreen props
      unreadMessages={unreadMessages}
      reviewsPending={reviewsPending}
      reviewsRecent={reviewsRecent}
      onOpenDoctorProfile={handleOpenDoctorProfile}
      onBookDoctor={handleBookDoctor}
      onOpenAppointment={handleOpenAppointment}
      onOpenDocument={handleOpenDocument}
      onOpenMessages={handleOpenMessages}
      onWriteReview={handleWriteReview}
     
      onProfile={handleProfile}
      onLogout={() => auth.signOut()}
    />
  );
}
